import React, { useEffect } from "react";
import { Redirect, Route } from "react-router-dom";
import { IonApp, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { I18nextProvider, useTranslation } from "react-i18next";
import { LoadingSpinner } from "./components/common/LoadingSpinner";
import "./i18n";

// import PWAInstallModal from './components/PWAInstallModal';
import IntroModal from "./components/IntroModal";
// import { usePWAInstall } from './hooks/usePWAInstall';
import { useIntroModal } from "./hooks/useIntroModal";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";

import Login from "./components/Login";
import Register from "./components/Register";
import EmailVerification from "./components/EmailVerification";
import RussianTutor from "./components/RussianTutor";
import TeacherSelection from "./components/TeacherSelection";
import PaymentGateway from "./components/PaymentGateway";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import Dashboard from "./components/Dashboard";
import HomePage from "./components/HomePage";
import ConditionsGenerales from "./components/ConditionsGenerales";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./firebaseConfig";

setupIonicReact();

interface PrivateRouteProps {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
  ...rest
}) => {
  const [user, loading] = useAuthState(auth);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" color="#31D526" />
      </div>
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!user) {
          // Not logged in, redirect to login page
          return <Redirect to="/login" />;
        }

        if (!user.emailVerified) {
          // Email not verified, redirect to verification page
          return <Redirect to="/email-verification" />;
        }

        // Authorized, render component
        return (
          <>
            <Component {...props} />
          </>
        );
      }}
    />
  );
};

const App: React.FC = () => {
  const [user, loading] = useAuthState(auth);
  const { i18n } = useTranslation();
  // const { showInstallPrompt, closePrompt } = usePWAInstall();
  const { showIntroModal, closeIntroModal } = useIntroModal();

  useEffect(() => {
    if (user) {
      // You can add any user-specific initialization here
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" color="#31D526" />
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <IonApp>
        {/* <PWAInstallModal isOpen={showInstallPrompt} onClose={closePrompt} /> */}
        <IntroModal isOpen={showIntroModal} onClose={closeIntroModal} />
        <IonReactRouter>
          <IonRouterOutlet>
            <Route exact path="/login" component={Login} />
            <Route exact path="/register" component={Register} />
            <Route
              exact
              path="/email-verification"
              component={EmailVerification}
            />
            <Route exact path="/home" component={HomePage} />
            <Route exact path="/condition-general" component={ConditionsGenerales} />
            <Route
              path="/"
              render={() => {
                if (user) {
                  if (!user.emailVerified) {
                    return <Redirect to="/email-verification" />;
                  }
                  return <Redirect to="/teacher-selection" />;
                }
                return <Redirect to="/home" />;
              }}
              exact={true}
            />
            <PrivateRoute
              exact
              path="/teacher-selection"
              component={TeacherSelection}
            />
            <PrivateRoute
              exact
              path="/tutor/:teacherId"
              component={RussianTutor}
            />
            <PrivateRoute exact path="/payment" component={PaymentGateway} />
            <PrivateRoute
              exact
              path="/payment/success"
              component={PaymentSuccess}
            />
            <PrivateRoute
              exact
              path="/payment/cancel"
              component={PaymentCancel}
            />
            <PrivateRoute exact path="/dashboard" component={Dashboard} />
          </IonRouterOutlet>
        </IonReactRouter>
      </IonApp>
    </I18nextProvider>
  );
};

export default App;
